import { AxiosError } from 'axios';
import { ActionTree, Module, MutationTree } from 'vuex';

import {
  FactoryFreightCosts,
  FreightCostImportError
} from '../../../shared/types/freightCost/freightCostXlsx';
import { buildFactorySheetRows } from '../../../shared/utils/freightCost/freightCostExport';
import { validateImportedSheets } from '../../../shared/utils/freightCost/freightCostImport';
import confirm from '../helpers/confirm';
import downloadFreightCostExport from '../helpers/freightCostXlsxExport';
import readFreightCostSheets from '../helpers/freightCostXlsxImport';
import ApiService from '../services/ApiService';
import { RootState } from './';

type State = {
  isExportLoading: boolean;
  isImportLoading: boolean;
  // problem of the file itself, shown under the upload field
  fileError: string;
  // problems of what the file holds, shown in the error dialog
  importErrors: FreightCostImportError[];
};

const state: State = {
  isExportLoading: false,
  isImportLoading: false,
  fileError: '',
  importErrors: []
};

/**
 * Asks the user to confirm the upload, listing what it changes.
 * The factories following the default freight costs are named apart.
 *
 * @param {FactoryFreightCosts[]} factories the freight costs read from the file,
 * factory by factory
 * @param {string[]} factoryIdsUsingDefaultCosts identifiers of the factories
 * that follow the default freight costs
 * @returns {Promise<boolean>} whether the user confirmed it
 */
async function confirmImport(
  factories: FactoryFreightCosts[],
  factoryIdsUsingDefaultCosts: string[]
): Promise<boolean> {
  const factoryIds = factories.map((factory) => factory.factoryId);

  const switchingIds = factoryIds.filter((factoryId) =>
    factoryIdsUsingDefaultCosts.includes(factoryId)
  );
  const switchingWarning = switchingIds.length
    ? `<br /><br /><i>Info: azok a gyárak, 
    amelyek eddig alapértelmezett fuvardíjakat használtak,
    ezután saját díjtáblával fognak számolni: ${switchingIds.join(', ')}.</i>`
    : '';

  return !!(await confirm(
    `A feltöltés a következő gyárak fuvardíjait írja felül:<br />` +
      `<b>${factoryIds.join(', ')}</b>.` +
      switchingWarning,
    {
      title: 'Gyárak fuvardíjainak módosítása',
      color: 'primary',
      icon: 'info-outlined',
      width: 500,
      buttonTrueText: 'Feltöltés',
      buttonFalseText: 'Mégsem'
    }
  ));
}

const actions: ActionTree<State, RootState> = {
  /**
   * Downloads the freight costs of the active factories in an xlsx file:
   * one sheet per factory, filled with its own freight costs.
   * The sheet of a factory using the default freight costs stays empty.
   * The edited file's format can be used for uploading back.
   */
  async downloadExport({ commit, dispatch, rootState }) {
    commit('setIsExportLoading', true);

    try {
      const freightCostsByFactory: FactoryFreightCosts[] = (
        await ApiService.getFreightCostsByFactory()
      ).data;
      if (!freightCostsByFactory?.length) {
        dispatch('alert/error', 'Nincs aktív gyár, amelyhez fuvardíjat lehetne letölteni!', {
          root: true
        });
        return;
      }

      const freightCostTypes = rootState.app.config?.freightCostTypes ?? {};
      downloadFreightCostExport(
        freightCostsByFactory.map((factoryFreightCosts) => ({
          name: factoryFreightCosts.factoryId,
          rows: buildFactorySheetRows(freightCostTypes, factoryFreightCosts.types)
        }))
      );
    } catch (error) {
      // request errors are already shown to the user by the response interceptor
      if (!(error instanceof AxiosError)) {
        dispatch('alert/error', 'A fuvardíjak letöltése nem sikerült!', { root: true });
      }
    } finally {
      commit('setIsExportLoading', false);
    }
  },

  /**
   * Reads an uploaded xlsx file, checks it, and saves the freight costs it holds.
   * Every problem of the file is collected and shown at once,
   * and nothing is saved until the file is errorless.
   *
   * @param {File} file the uploaded file
   */
  async importFile({ commit, dispatch, rootGetters, rootState }, file: File) {
    commit('setFileError', '');
    commit('setImportErrors', []);
    commit('setIsImportLoading', true);

    try {
      /**
       * The sheet names are resolved against the factories,
       * so the factory list has to be a fresh one
       */
      await dispatch('factories/get', undefined, { root: true });

      const sheets = await readFreightCostSheets(file).catch(() => undefined);

      /*
       * The format of the file is checked on the field already, so what fails here is a file
       * that only looks like a spreadsheet - it is a problem of the file, not of its content.
       */
      if (!sheets) {
        commit('setFileError', 'A fájl nem olvasható, lehet, hogy sérült!');
        return;
      }

      const { errors, factories } = validateImportedSheets(
        sheets,
        rootState.app.config?.freightCostTypes ?? {},
        { factoryShortNames: rootGetters['factories/factoryShortNames'] ?? [] }
      );

      if (errors.length) {
        commit('setImportErrors', errors);
        return;
      }

      // Confirm the import of the checked freight costs
      const factoryIdsUsingDefaultCosts = rootGetters['factories/factories']
        .filter((factory) => factory.useDefaultFreightCostsSettings)
        .map((factory) => factory.shortName ?? '');

      if (!(await confirmImport(factories, factoryIdsUsingDefaultCosts))) {
        return;
      }

      // the server answers with the factories it has written
      const { data } = await ApiService.importFreightCosts(factories);

      dispatch(
        'alert/success',
        `A fuvardíjak frissültek. Érintett gyárak: ${data.factoryIds.join(', ')}`,
        { root: true }
      );
      await dispatch('factories/get', undefined, { root: true });
      await dispatch('freightFees/refresh', undefined, { root: true });
    } catch (error) {
      /*
        The server checks the freight costs again with its own factories,
        so it can reject them with its own list of problems - those go to the same dialog.
        It happens when the stored data changed since the file was checked here.
      */
      const serverErrors = (error as AxiosError<{ errors?: FreightCostImportError[] }>).response
        ?.data?.errors;

      if (serverErrors?.length) {
        commit('setImportErrors', serverErrors);
      } else {
        dispatch('alert/error', 'A fuvardíjak feltöltése nem sikerült!', { root: true });
      }
    } finally {
      commit('setIsImportLoading', false);
    }
  },

  clearImportErrors({ commit }) {
    commit('setImportErrors', []);
  },

  clearFileError({ commit }) {
    commit('setFileError', '');
  }
};

const mutations: MutationTree<State> = {
  setFileError(state, fileError: string) {
    state.fileError = fileError;
  },
  setIsExportLoading(state, isExportLoading: boolean) {
    state.isExportLoading = isExportLoading;
  },
  setIsImportLoading(state, isImportLoading: boolean) {
    state.isImportLoading = isImportLoading;
  },
  setImportErrors(state, importErrors: FreightCostImportError[]) {
    state.importErrors = importErrors;
  }
};

export const freightCostsXlsx: Module<State, RootState> = {
  namespaced: true,
  state,
  actions,
  mutations
};
